import React from "react";
import { companyPluginUseCase } from "../../../../di/container";
import type {
  CompanyPlugin,
  CompanyPluginType,
} from "../../../../domain/types/company-plugin";
import type { AnyCompanyPluginDefinition } from "./types";
import { COMPANY_PLUGIN_DEFINITIONS } from "./plugin-definitions";
import { resolveErrorMessage } from "../../../pages/CompanyAdmin/MallDesign/utils";

interface CompanyPluginSectionProps {
  companyId: number;
  /** 저장/해제 결과 알림 (페이지의 NotificationList 로 띄운다) */
  onNotify: (message: string) => void;
}

/** 열려 있는 다이얼로그: 어떤 플러그인을, 신규 등록인지 수정인지 */
type OpenDialog = { type: CompanyPluginType; isEditing: boolean } | null;

const findDefinition = (type: CompanyPluginType) =>
  COMPANY_PLUGIN_DEFINITIONS.find((definition) => definition.type === type);

/**
 * 내 회사 정보 > 추가 서비스 연동.
 *
 * 등록된 플러그인을 카드 그리드로 보여주고, 우측 상단 + 버튼으로 아직 등록하지 않은 플러그인을 추가한다.
 * 카드를 누르면 같은 다이얼로그가 수정 모드로 열리고, 거기서 연동 해제도 한다.
 *
 * 플러그인은 회사 정보 폼의 '저장' 과 별개로 **다이얼로그에서 저장하는 즉시 반영**된다
 * (자체 API 가 있고, 폼 저장을 기다리게 하면 등록했는데 사라지는 것처럼 보인다).
 */
export const CompanyPluginSection = ({
  companyId,
  onNotify,
}: CompanyPluginSectionProps) => {
  const [plugins, setPlugins] = React.useState<CompanyPlugin[]>([]);
  const [isLoaded, setIsLoaded] = React.useState(false);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [isAddMenuOpen, setIsAddMenuOpen] = React.useState(false);
  const [openDialog, setOpenDialog] = React.useState<OpenDialog>(null);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const fetched = await companyPluginUseCase.getPlugins(companyId);
        if (!cancelled) setPlugins(fetched);
      } catch (error) {
        if (!cancelled) {
          setLoadError(
            resolveErrorMessage(error, "연동 서비스 목록을 불러오지 못했습니다."),
          );
        }
      } finally {
        if (!cancelled) setIsLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [companyId]);

  const registeredTypes = new Set(plugins.map((plugin) => plugin.pluginType));
  // 이미 등록된 서비스는 + 목록에서 감춘다 (종류별 1개)
  const addableDefinitions = COMPANY_PLUGIN_DEFINITIONS.filter(
    (definition) => !registeredTypes.has(definition.type),
  );

  const handleSaved = (saved: CompanyPlugin) => {
    const wasEditing = openDialog?.isEditing ?? false;
    setPlugins((previous) =>
      previous.some((plugin) => plugin.pluginType === saved.pluginType)
        ? previous.map((plugin) =>
            plugin.pluginType === saved.pluginType ? saved : plugin,
          )
        : [...previous, saved],
    );
    setOpenDialog(null);
    const name = findDefinition(saved.pluginType)?.name ?? "서비스";
    onNotify(wasEditing ? `${name} 연동을 수정했습니다.` : `${name} 연동을 등록했습니다.`);
  };

  const handleDeleted = (pluginType: CompanyPluginType) => {
    setPlugins((previous) =>
      previous.filter((plugin) => plugin.pluginType !== pluginType),
    );
    setOpenDialog(null);
    onNotify(`${findDefinition(pluginType)?.name ?? "서비스"} 연동을 해제했습니다.`);
  };

  const renderDialog = () => {
    if (!openDialog) return null;
    const definition = findDefinition(openDialog.type);
    if (!definition) return null;
    const saved = plugins.find((plugin) => plugin.pluginType === openDialog.type);
    return renderPluginDialog(definition, {
      companyId,
      initialConfig: openDialog.isEditing && saved ? saved.config : null,
      onClose: () => setOpenDialog(null),
      onSaved: handleSaved,
      onDeleted: handleDeleted,
    });
  };

  return (
    <>
      <div className="w-full / flex flex-row justify-between items-center gap-1 mb-[2.0313rem]">
        <h3 className="font-semibold">추가 서비스 연동</h3>

        <div className="relative">
          <button
            type="button"
            aria-label="연동 서비스 추가"
            aria-haspopup="menu"
            aria-expanded={isAddMenuOpen}
            onClick={() => setIsAddMenuOpen((open) => !open)}
            className="flex items-center justify-center w-[32px] h-[32px] rounded-[8px]
              border border-[#e0e0e0] bg-white hover:bg-[#f5f5f5] transition-all"
          >
            <img src="/assets/icons/Plus.svg" alt="" className="w-[16px] h-[16px]" />
          </button>

          {isAddMenuOpen && (
            <>
              {/* 바깥을 누르면 닫힌다 */}
              <div
                className="fixed inset-0 z-[40]"
                onClick={() => setIsAddMenuOpen(false)}
              />
              <div
                role="menu"
                className="absolute right-0 top-[40px] z-[41] w-[18rem]
                  bg-white border border-[#e0e0e0] rounded-[12px] shadow-[0_4px_16px_rgba(0,0,0,0.08)] p-[0.5rem]"
              >
                {addableDefinitions.length === 0 ? (
                  <div className="px-[0.75rem] py-[0.625rem] text-[13px] text-[#888] text-start">
                    추가할 수 있는 서비스가 없습니다.
                  </div>
                ) : (
                  addableDefinitions.map((definition) => (
                    <button
                      key={definition.type}
                      type="button"
                      role="menuitem"
                      onClick={() => {
                        setIsAddMenuOpen(false);
                        setOpenDialog({ type: definition.type, isEditing: false });
                      }}
                      className="w-full flex items-start gap-[0.625rem] px-[0.75rem] py-[0.625rem]
                        rounded-[8px] text-start hover:bg-[#E8EFFF] transition-all"
                    >
                      <img
                        src={definition.iconSrc}
                        alt=""
                        className="w-[28px] h-[28px] rounded-[6px] shrink-0"
                      />
                      <div>
                        <div className="text-[14px] font-medium text-[#222]">
                          {definition.name}
                        </div>
                        <div className="text-[12px] text-[#888]">
                          {definition.description}
                        </div>
                      </div>
                    </button>
                  ))
                )}
              </div>
            </>
          )}
        </div>
      </div>

      <div className="pl-0 md:pl-5 / w-full max-w-[45rem] mb-[2.0313rem]">
        {loadError ? (
          <div className="p-[0.875rem] rounded-[8px] bg-[#EBF3FF] text-[#4285F4] text-[13px] text-start">
            {loadError}
          </div>
        ) : isLoaded && plugins.length === 0 ? (
          <div className="p-[1.25rem] rounded-[12px] border border-dashed border-[#e0e0e0] text-[13px] text-[#888] text-center">
            연동된 서비스가 없습니다. 우측 상단 + 버튼으로 추가해 주세요.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-[0.75rem]">
            {plugins.map((plugin) => {
              const definition = findDefinition(plugin.pluginType);
              if (!definition) return null;
              return (
                <button
                  key={plugin.pluginType}
                  type="button"
                  onClick={() =>
                    setOpenDialog({ type: plugin.pluginType, isEditing: true })
                  }
                  className="flex items-start gap-[0.75rem] p-4 text-start
                    bg-white border border-[#e0e0e0] rounded-[12px] hover:border-[#4285F4] transition-all"
                >
                  <img
                    src={definition.iconSrc}
                    alt=""
                    className="w-[36px] h-[36px] rounded-[8px] shrink-0"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-[0.375rem]">
                      <span className="text-[15px] font-medium text-[#222]">
                        {definition.name}
                      </span>
                      <span className="px-[6px] py-[1px] rounded-[4px] bg-[#E8EFFF] text-[#4285F4] text-[11px] font-medium">
                        연동됨
                      </span>
                    </div>
                    <div className="text-[13px] text-[#888] truncate">
                      {summarizePlugin(definition, plugin)}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {renderDialog()}
    </>
  );
};

/*
  정의 배열은 플러그인 종류의 합집합이라, 정의와 저장된 값의 종류가 같다는 것을 TS 가 스스로 알지 못한다.
  종류를 맞춰 찾은 뒤(findDefinition) 넘기므로 안전하다 — 좁히는 캐스팅을 이 두 곳에만 가둔다.
*/
const summarizePlugin = (
  definition: AnyCompanyPluginDefinition,
  plugin: CompanyPlugin,
): string =>
  (definition.summarize as (config: CompanyPlugin["config"]) => string)(
    plugin.config,
  );

const renderPluginDialog = (
  definition: AnyCompanyPluginDefinition,
  props: {
    companyId: number;
    initialConfig: CompanyPlugin["config"] | null;
    onClose: () => void;
    onSaved: (plugin: CompanyPlugin) => void;
    onDeleted: (pluginType: CompanyPluginType) => void;
  },
) => {
  const DialogComponent = definition.Dialog as React.ComponentType<
    typeof props
  >;
  return <DialogComponent {...props} />;
};
